/**
 * Parse une date au format YYYY-MM-DD en utilisant le fuseau horaire local
 * Évite les problèmes de décalage de date causés par parseISO qui interprète en UTC
 * Gère les formats de date PostgreSQL (YYYY-MM-DD et timestamps ISO)
 * @param {string} dateString - Date au format YYYY-MM-DD ou timestamp PostgreSQL
 * @returns {Date|null} - Date object ou null si invalide
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // Si c'est déjà un objet Date, le retourner tel quel
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }
  
  // Convertir en string
  const dateStr = String(dateString);
  
  // Extraire juste la partie date si c'est un timestamp PostgreSQL (2024-01-15T00:00:00.000Z)
  const datePart = dateStr.split('T')[0];
  
  // Parser les composants de la date
  const parts = datePart.split('-');
  if (parts.length !== 3) return null;
  
  const [year, month, day] = parts.map(Number);
  
  // Vérifier que les valeurs sont valides
  if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  
  // Créer la date en utilisant le fuseau horaire local (month est 0-indexé)
  const date = new Date(year, month - 1, day);
  
  // Vérifier que la date créée est valide
  if (isNaN(date.getTime())) {
    return null;
  }
  
  return date;
};

/**
 * Compare deux dates en ignorant l'heure
 * @param {Date|string} date1 - Première date
 * @param {Date|string} date2 - Deuxième date
 * @returns {boolean} - True si les dates sont le même jour
 */
export const isSameCalendarDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  
  const d1 = typeof date1 === 'string' ? parseLocalDate(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseLocalDate(date2) : date2;
  
  if (!d1 || !d2) return false;
  
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};